/**
 * Reference: https://github.com/stacktracejs/stackframe
 * LICENSE
 * Copyright (c) 2017 Eric Wendelin and other contributors
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy of
 * this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to
 * use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies
 * of the Software, and to permit persons to whom the Software is furnished to do
 * so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */
import { IStackFrame } from '../types';

function _isNumber (n: any) {
  return !isNaN(parseFloat(n)) && isFinite(n);
}

const booleanProps = ['isConstructor', 'isEval', 'isNative', 'isToplevel'];
const numericProps = ['columnNumber', 'lineNumber'];
const stringProps = ['fileName', 'functionName', 'source'];
const arrayProps = ['args'];

const props = booleanProps.concat(numericProps, stringProps, arrayProps);

export default class StackFrame {
  isConstructor?: boolean;
  isEval?: boolean;
  isNative?: boolean;
  isToplevel?: boolean;
  columnNumber?: number;
  lineNumber?: number;
  fileName?: string;
  functionName?: string;
  source?: string;
  args?: any[];
  [key: string]: any;

  constructor(obj: IStackFrame) {
    for (const prop of props) {
      if (obj.hasOwnProperty(prop) && obj[prop] !== undefined) {
        if (booleanProps.includes(prop)) {
          this[prop] = Boolean(obj[prop]);
        } else if (arrayProps.includes(prop)) {
          if (Object.prototype.toString.call(obj[prop]) !== '[object Array]') {
            throw new TypeError('Args must be an Array');
          }
          this.args = obj[prop];
        } else if (numericProps.includes(prop)) {
          if (!_isNumber(obj[prop])) {
            throw new TypeError(prop + ' must be a Number');
          }
          this[prop] = Number(obj[prop]);
        } else {
          this[prop] = String(obj[prop]);
        }
      }
    }
  }

  toString() {
    const functionName = this.functionName || '{anonymous}';
    const args = '(' + (this.args || []).join(',') + ')';
    const fileName = this.fileName ? ('@' + this.fileName) : '';
    const lineNumber = _isNumber(this.lineNumber) ? (':' + this.lineNumber) : '';
    const columnNumber = _isNumber(this.columnNumber) ? (':' + this.columnNumber) : '';
    return functionName + args + fileName + lineNumber + columnNumber;
  }

  static fromString(str: string) {
    const argsStartIndex = str.indexOf('(');
    const argsEndIndex = str.lastIndexOf(')');

    const functionName = str.substring(0, argsStartIndex);
    const args = str.substring(argsStartIndex + 1, argsEndIndex).split(',');
    const locationString = str.substring(argsEndIndex + 1);

    let fileName: string | undefined;
    let lineNumber: number | undefined;
    let columnNumber: number | undefined;

    if (locationString.indexOf('@') === 0) {
      const parts = /@(.+?)(?::(\d+))?(?::(\d+))?$/.exec(locationString);
      if (parts) {
        fileName = parts[1];
        lineNumber = parts[2] ? Number(parts[2]) : undefined;
        columnNumber = parts[3] ? Number(parts[3]) : undefined;
      }
    }

    return new StackFrame({
      functionName,
      args,
      fileName,
      lineNumber: lineNumber || undefined,
      columnNumber: columnNumber || undefined,
    });
  }
}
